import { useMemo } from 'react';
import React from 'react';
import {
  AgenticProps as SiteComponentsAgenticProps,
  AgenticConfig,
  FeatureFlags as SiteComponentsFeatureFlags,
} from 'value-digital-site-components';
import { FeatureFlags as AppFeatureFlags } from '@/store/contexts/featureFlagContext';

interface UseAgenticConfigParams {
  dispatch: NonNullable<NonNullable<SiteComponentsAgenticProps['config']>['callbacks']>['dispatch'];
  featureFlags?: AppFeatureFlags | null;
  brand?: string;
  deviceInfo?: { deviceId?: string; deviceTitle?: string };
  deviceType?: string;
  pageName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allFilters?: any;
  refreshCart?: () => void;
  refreshOrderSummary?: () => void;
  productsTitleRef?: React.RefObject<HTMLDivElement | null>;
  /** Nav API `agenticChat` payload for site-components Agentic */
  agenticChat?: unknown;
  /** Native app webview — passed through to site-components Agentic config */
  appWebview?: boolean;
}

// The library maps brand keys: lowercase → uppercase (e.g. "totalwireless" → "TOTAL_WIRELESS"),
// then applies class `vd-sc-brand-${mapped}-agentic`. CSS selectors use the lowercase form,
// so we must pass the uppercase key so the mapping produces the lowercase CSS class name.
const BRAND_TO_LIBRARY_KEY: Record<string, string> = {
  totalwireless: 'TOTAL_WIRELESS',
  visible: 'VISIBLE',
  straighttalk: 'STRAIGHT_TALK',
  tracfone: 'TRACFONE',
  walmartfamilymobile: 'WFM',
  simplemobile: 'SIMPLEMOBILE',
};

export const useAgenticConfig = ({
  dispatch,
  featureFlags,
  brand,
  deviceInfo,
  deviceType,
  pageName,
  allFilters,
  refreshCart,
  refreshOrderSummary,
  productsTitleRef,
  agenticChat,
  appWebview,
}: UseAgenticConfigParams): { config: AgenticConfig } => {
  const libraryBrand = BRAND_TO_LIBRARY_KEY[brand?.toLowerCase() ?? ''] ?? brand;

  return useMemo(
    () => ({
      config: {
        brand: libraryBrand as AgenticConfig['brand'],
        callbacks: {
          dispatch,
          ...(refreshCart ? { refreshCart } : {}),
          ...(refreshOrderSummary ? { refreshOrderSummary } : {}),
        },
        functionality: {
          ...(deviceInfo ? { DEVICE_INFO: deviceInfo } : {}),
          ...(deviceType ? { DEVICE_TYPE: deviceType } : {}),
          ...(pageName ? { PAGE_NAME: pageName } : {}),
          ...(allFilters ? { ALL_FILTERS: allFilters } : {}),
        },
        refs: {
          ...(productsTitleRef ? { productsTitleRef } : {}),
        },
        featureFlags: (featureFlags ?? {}) as SiteComponentsFeatureFlags,
        ...(agenticChat !== undefined ? { agenticChat } : {}),
        ...(appWebview ? { appWebview } : {}),
      } as AgenticConfig,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, featureFlags, brand, deviceInfo, deviceType, pageName, allFilters, refreshCart, refreshOrderSummary, productsTitleRef, agenticChat, appWebview]
  );
};
